import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Manager, Reference, Popper } from 'react-popper';
import useStyle from './popoverStyles';
import EventSetter from './EventSetter';
import usePlacement from './usePlacement';

/**
 * Popover element for use in Cvent-branded interfaces. Requires a trigger prop following
 * the render prop pattern to open and close the popover.
 * The contents of the popover will be rendered from the popover's children.
 */

export const Popover = ({
  children,
  trigger,
  accessibilityLabel,
  accessible = true,
  defaultOpen = false,
  hasPadding = true,
  isRtl = false,
  maxHeight,
  nonAnimated = false,
  onClose = () => {},
  onOpen = () => {},
  placement = 'bottom-start',
  portal,
  preventOverflow = true,
  testID,
  useFixed = false,
  zIndex,
  ...propDrop
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const containerRef = useRef({});
  const position = usePlacement(placement, isRtl);

  const { mainContainer, contentContainer, popoverContainer } = useStyle(
    isRtl,
    isOpen,
    nonAnimated,
    zIndex,
    hasPadding
  );

  const handleClick = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  };

  const handleClose = () => {
    if (isOpen) {
      setIsOpen(false);
      onClose();
    }
  };

  const renderPopper = () => {
    const portalRef = portal;
    const popperAndContent = (
      <Popper
        {...propDrop}
        placement={position}
        positionFixed={useFixed}
        modifiers={{
          preventOverflow: {
            enabled: preventOverflow
          },
          hide: {
            enabled: preventOverflow
          },
          flip: {
            // If maxHeight is dynamic, we have to disable flip as boundry overlap
            // Is always triggered and therefore always in a flipped state.
            enabled: maxHeight !== true && preventOverflow
          },
          maxHeight: {
            enabled: !!maxHeight,
            order: 800,
            fn: dataObject => {
              // Static offset provides bottom padding when dynamic height is used.
              const STATIC_OFFSET = 16;

              // Static height as fallback

              const MIN_HEIGHT_THRESHOLD = 300;

              // Viewport height determines absolute max

              const viewportHeight =
                dataObject.instance.state.scrollParents[0]?.innerHeight;

              // If viewport is smaller than static threshold, use that instead

              const minHeightMax =
                MIN_HEIGHT_THRESHOLD >= viewportHeight
                  ? viewportHeight
                  : MIN_HEIGHT_THRESHOLD;

              // Popper inner content container height reflects the absolute minimum height
              const popperInnerHeight =
                dataObject.instance.popper?.firstChild?.offsetHeight;

              const minHeightMin =
                MIN_HEIGHT_THRESHOLD >= popperInnerHeight
                  ? popperInnerHeight
                  : MIN_HEIGHT_THRESHOLD;

              // Computed minimum height
              // if viewport is smaller than static threshold use that
              // if content is smaller than either of those, use that instead

              const computedMinHeight =
                minHeightMax >= minHeightMin ? minHeightMin : minHeightMax;

              // Computed maximum height
              // if user provides a number, apply directly
              // if true, compute delta between popper top viewport height

              let computedMaxHeight;

              if (typeof maxHeight === 'number') {
                computedMaxHeight = maxHeight;
              } else if (maxHeight === true) {
                const popperTop = dataObject.offsets.popper?.top;

                if (viewportHeight && popperTop) {
                  computedMaxHeight =
                    viewportHeight - popperTop - STATIC_OFFSET;
                }
              }

              if (computedMaxHeight) {
                // eslint-disable-next-line no-param-reassign
                dataObject.styles.maxHeight = `${computedMaxHeight}px`;
                // eslint-disable-next-line no-param-reassign
                dataObject.styles.minHeight = `${computedMinHeight}px`;
              }

              return dataObject;
            }
          }
        }}
      >
        {({ ref, style, dataPlacement }) => (
          <div
            ref={ref}
            style={style}
            data-placement={dataPlacement}
            css={popoverContainer}
          >
            <EventSetter containerRef={containerRef} onDismiss={handleClose}>
              <div
                ref={containerRef}
                css={contentContainer}
                aria-label={accessibilityLabel}
                aria-hidden={!accessible}
              >
                {typeof children === 'function'
                  ? children({ closePopover: handleClose })
                  : children}
              </div>
            </EventSetter>
          </div>
        )}
      </Popper>
    );
    if (portalRef) {
      const portalTarget =
        portalRef === true ? window.document.body : portalRef;
      // portal is wrapped in div to suppress prop-type warning from Manager
      return ReactDOM.createPortal(popperAndContent, portalTarget);
    }
    return popperAndContent;
  };

  return (
    <Manager>
      <Reference>
        {({ targetProps, ref }) => (
          <div ref={ref}>
            {trigger({
              targetProps,
              toggleOpen: handleClick,
              isOpen,
              closePopover: handleClose
            })}
          </div>
        )}
      </Reference>
      {isOpen && (
        <div css={mainContainer} {...injectTestId(testID)}>
          {renderPopper()}
        </div>
      )}
    </Manager>
  );
};

Popover.propTypes = {
  /** Popover will display children within overlay */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func
  ]).isRequired,
  /**
   * Render props function to display the trigger component.
   * Function parameters available: targetProps, toggleOpen, isOpen and closePopover.
   * For example:
   * ```
   * trigger={({ targetProps, toggleOpen, isOpen }) => (
   *  <button type="button" {...targetProps} onClick={toggleOpen}>
   *    Am I open? { isOpen ? 'Yes!' : 'No!' }
   *  </button>
   * )}
   * ```
   */
  trigger: PropTypes.func.isRequired,
  /** Screen reader label text (e.g. aria-label) */
  accessibilityLabel: PropTypes.string,
  /** Hidden from screen reader (e.g. aria-hidden) */
  accessible: PropTypes.bool,
  /** defaultOpen will set the popover to open upon component mount */
  defaultOpen: PropTypes.bool,
  /** Text direction */
  isRtl: PropTypes.bool,
  /** Set a maxHeight for the popover container causing overflowing content to scroll internally.
   * Passing a `number` will set a maxHeight statically.
   * Passing `true` will will dynamically size the popover container to viewport bounds if content is greater than.
   * Note: Passing `true` is intended for popovers appearing at the top of the page with long content. It is not intended for use
   * when menu appears inline or at the bottom of the page. Prefer static maxHeight if necessary for these use cases.
   * */
  maxHeight: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  /** Set to true to turn off initial popover container animation. */
  nonAnimated: PropTypes.bool,
  /** Call a function upon the Popover closing. No return value. */
  onClose: PropTypes.func,
  /** Call a function upon the Popover opening. No return value. */
  onOpen: PropTypes.func,
  /** Add default padding of 16px to popover. */
  hasPadding: PropTypes.bool,
  /** Initial placement for the popover. Default is bottomStart.
   * Constants for these values can be imported from the Placements module
   * `import { Placements } from '@cvent/carina-popover'`
   */
  placement: PropTypes.oneOf([
    'auto',
    'top',
    'top-start',
    'top-end',
    'right',
    'right-start',
    'right-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
    'end',
    'end-top',
    'end-bottom',
    'start',
    'start-top',
    'start-bottom'
  ]),
  /** DOM node reference to render menu inside. If boolean 'true' is provided, Menu will be appended to the 'body' element. */
  portal: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  /** Set preventOverflow to true if you wish the Popover to flow outside the container */
  preventOverflow: PropTypes.bool,
  /** For e2e tests (maps to `data-cvent-id`) */
  testID: PropTypes.string,
  /** useFixed will switch popover positioning from absolute to fixed.
   *  If you''re seeing the popover affect surrounding containers or cause overflow problems,
   *  use this setting to possibly resolve.
   */
  useFixed: PropTypes.bool,
  /** zIndex allows the user to alter Popover's default z-index */
  zIndex: PropTypes.number
};
